import { faArrowLeft, faChevronDown, faChevronUp, faExclamationTriangle, faSave } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import moment from "moment";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Button, ButtonGroup, Col, FormGroup, FormSelect, Row, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { injectIntl } from "react-intl";
import { QuestionTypes } from "../../../common/constant";
import {
  createWarningMessages
} from "../../../ducks/message";
import { getQuestionsFromSurvey } from "../../../ducks/surveyEditor";
import rest from "../../../common/rest";
import SurveyUtils from "../../../utils/SurveyUtils";
import StandardView from "../../common/StandardView";
import SurveyVersionComparisonTable from "./SurveyVersionComparisonTable";

const typeOrder = ["pages", "questions", ["rows", "columns", "choices"], "choices"];
const COMPARISON_MODE_PAGES = "pages";
const COMPARISON_MODE_QUESTIONS = "questions";

class SurveyVersionCompare extends Component {
  constructor(props) {
    super(props);

    this.duplicateKeysWarningShown = false;
    this.duplicatePageKeysWarningShownFor = new Set();

    let surveys = props.surveys?.length ? [...props.surveys] : [];
    if (surveys.length && props.latestSurvey && surveys[0].version !== props.latestSurvey.version) {
      surveys.unshift(props.latestSurvey);
    }

    const surveyVersion = surveys[1] ?? props.lastApprovedSurvey;
    const surveyVersionToCompare = props.latestSurvey;

    this.state = {
      surveys: surveys,
      surveyVersion,
      surveyVersionToCompare,
      comparisonMode: COMPARISON_MODE_PAGES,
      loadingSelection: null,
      expandedPages: {},
      expandedQuestions: {},
      comparisonOptions: this.buildComparisonOptions(props, surveys, surveyVersion, surveyVersionToCompare)
    };
  }

  componentDidMount() {
    this.showDuplicateKeysWarningIfNeeded();
    this.showDuplicatePageKeysWarningIfNeeded();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.surveys !== this.props.surveys ||
      prevProps.latestSurvey !== this.props.latestSurvey ||
      prevProps.lastApprovedSurvey !== this.props.lastApprovedSurvey ||
      prevProps.templateList !== this.props.templateList
    ) {
      const surveys = this.buildSurveyHistory(this.props);
      const surveyVersion = this.getNextSelectedSurvey(
        this.state.surveyVersion,
        surveys[1] ?? this.props.lastApprovedSurvey
      );
      const surveyVersionToCompare = this.getNextSelectedSurvey(
        this.state.surveyVersionToCompare,
        this.props.latestSurvey
      );

      this.setState({
        surveys,
        surveyVersion,
        surveyVersionToCompare,
        comparisonOptions: this.buildComparisonOptions(this.props, surveys, surveyVersion, surveyVersionToCompare)
      }, () => {
        this.showDuplicateKeysWarningIfNeeded();
        this.showDuplicatePageKeysWarningIfNeeded();
      });

      return;
    }

    this.showDuplicateKeysWarningIfNeeded();
    this.showDuplicatePageKeysWarningIfNeeded();
  }

  buildSurveyHistory(props) {
    let surveys = props.surveys?.length ? [...props.surveys] : [];

    if (surveys.length && props.latestSurvey && surveys[0].version !== props.latestSurvey.version) {
      surveys.unshift(props.latestSurvey);
    }

    return surveys;
  }

  buildComparisonOptions(props, surveys, surveyVersion, surveyVersionToCompare) {
    const optionMap = new Map();
    const allSurveys = [
      ...(props.templateList ?? []),
      ...(surveys ?? []),
      props.latestSurvey,
      props.lastApprovedSurvey,
      surveyVersion,
      surveyVersionToCompare
    ].filter(Boolean);

    allSurveys.forEach((survey) => {
      optionMap.set(this.getSurveySelectionKey(survey), survey);
    });

    return Array.from(optionMap.values()).sort((a, b) => {
      const surveyNameA = (a.surveyName ?? a.name ?? "").toLowerCase();
      const surveyNameB = (b.surveyName ?? b.name ?? "").toLowerCase();

      if (surveyNameA !== surveyNameB) {
        return surveyNameA.localeCompare(surveyNameB);
      }

      return (b.version ?? 0) - (a.version ?? 0);
    });
  }

  buildSurveyGroups(options) {
    const groups = new Map();

    options.forEach((survey) => {
      const groupKey = survey?.identifier ?? survey?.surveyId;
      if (!groupKey) {
        return;
      }

      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }

      groups.get(groupKey).push(survey);
    });

    return Array.from(groups.entries())
      .map(([identifier, surveys]) => ({
        identifier,
        surveyName: surveys[0]?.surveyName ?? surveys[0]?.name ?? "",
        surveys: surveys.sort((a, b) => (b.version ?? 0) - (a.version ?? 0))
      }))
      .sort((a, b) => a.surveyName.toLowerCase().localeCompare(b.surveyName.toLowerCase()));
  }

  getSurveyTitle(survey) {
    return survey?.surveyName ?? survey?.name ?? "";
  }

  getSurveyGroupTitle(group, selectedSurvey) {
    const selectedIdentifier = selectedSurvey?.identifier ?? selectedSurvey?.surveyId;

    if (group.identifier === selectedIdentifier) {
      return this.getSurveyTitle(selectedSurvey) || group.surveyName;
    }

    return group.surveyName;
  }

  getNextSelectedSurvey(currentSurvey, fallbackSurvey) {
    return currentSurvey?.surveyTemplate ? currentSurvey : fallbackSurvey;
  }

  getSurveySelectionKey(survey) {
    const identifier = survey?.identifier ?? survey?.surveyId;
    return `${identifier ?? "unknown"}-${survey?.version ?? "unknown"}`;
  }

  async handleSurveySelection(stateKey, selectionKey) {
    const selectedSurvey = this.state.comparisonOptions.find(
      (survey) => this.getSurveySelectionKey(survey) === selectionKey
    );

    if (!selectedSurvey) {
      return;
    }

    if (selectedSurvey.surveyTemplate) {
      this.setState({ [stateKey]: selectedSurvey });
      return;
    }

    const identifier = selectedSurvey.identifier ?? selectedSurvey.surveyId;
    if (!identifier || !selectedSurvey.version) {
      return;
    }

    this.setState({ loadingSelection: selectionKey });

    try {
      const loadedSurvey = await rest.doGet(`/admin/admin/surveys/${identifier}/version/${selectedSurvey.version}`);
      const comparisonOptions = this.buildComparisonOptions(
        this.props,
        this.state.surveys,
        stateKey === "surveyVersion" ? loadedSurvey : this.state.surveyVersion,
        stateKey === "surveyVersionToCompare" ? loadedSurvey : this.state.surveyVersionToCompare
      );

      this.setState({
        [stateKey]: loadedSurvey,
        comparisonOptions,
        loadingSelection: null
      });
    } catch (error) {
      this.setState({ loadingSelection: null });
    }
  }

  handleSurveyNameSelection(stateKey, identifier) {
    const selectedGroup = this.buildSurveyGroups(this.state.comparisonOptions).find((group) => group.identifier === identifier);

    if (!selectedGroup?.surveys?.length) {
      return;
    }

    this.handleSurveySelection(stateKey, this.getSurveySelectionKey(selectedGroup.surveys[0]));
  }

  isCurrentSurveySelected() {
    const { identifier, surveyVersionNumber } = this.props;
    const { surveyVersionToCompare } = this.state;
    const selectedIdentifier = surveyVersionToCompare?.identifier ?? surveyVersionToCompare?.surveyId;

    return (
      selectedIdentifier === identifier &&
      surveyVersionToCompare?.version === surveyVersionNumber
    );
  }

  getPages() {
    const { pages } = this.props;
    const { surveyVersion, surveyVersionToCompare } = this.state;
    const isCurrentSurveySelected = this.isCurrentSurveySelected();

    if (surveyVersionToCompare && surveyVersion) {
      const surveyVersionToComparePages = surveyVersionToCompare.surveyTemplate[typeOrder[0]];
      return isCurrentSurveySelected ? pages : surveyVersionToComparePages;
    }

    return []
  }

  getOldPages() {
    const { surveyVersion } = this.state;

    if (surveyVersion) {
      return surveyVersion.surveyTemplate[typeOrder[0]]
    }

    return []
  }

  buildSurveyComparison() {
    let res = [];
    const isLastVersionSelected = this.isCurrentSurveySelected();

    if (this.state.surveyVersionToCompare && this.state.surveyVersion) {
      if (this.state.comparisonMode === COMPARISON_MODE_QUESTIONS) {
        this.createQuestionComparisonTable(res);
      } else {
        this.createComparisonTable(this.getPages(), this.getOldPages(), res, 0);
      }
      this.detectChangesInChildren(res);
    }

    return { res, isLastVersionSelected };
  }

  getPageToggleIdentifier(page) {
    return `${page.nameNew || page.nameOld || ""}`;
  }

  getPageToggleIdentifiers(comparison) {
    return comparison
      .filter((row) => row.typeIndex === 0 && !row.emptyKey)
      .map((page) => this.getPageToggleIdentifier(page));
  }

  isPageExpanded(page) {
    const pageId = this.getPageToggleIdentifier(page);
    return this.state.expandedPages[pageId] !== false;
  }

  handleTogglePage(page) {
    const pageId = this.getPageToggleIdentifier(page);
    this.setState((state) => ({
      expandedPages: {
        ...state.expandedPages,
        [pageId]: state.expandedPages[pageId] === false
      }
    }));
  }

  handleSetAllPagesExpanded(comparison, expanded) {
    const expandedPages = {};
    this.getPageToggleIdentifiers(comparison).forEach((pageId) => {
      expandedPages[pageId] = expanded;
    });
    this.setState({ expandedPages });
  }

  getQuestionToggleIdentifier(question) {
    return `${question.pageName || ""}-${question.nameNew || question.nameOld || ""}`;
  }

  getQuestionToggleIdentifiers(comparison, questionIds = []) {
    comparison.forEach((row) => {
      if (row.typeIndex === 1 && !row.emptyKey) {
        questionIds.push(this.getQuestionToggleIdentifier(row));
      }

      if (row.children?.length) {
        this.getQuestionToggleIdentifiers(row.children, questionIds);
      }
    });

    return questionIds;
  }

  isQuestionExpanded(question) {
    const questionId = this.getQuestionToggleIdentifier(question);
    return this.state.expandedQuestions[questionId] !== false;
  }

  handleToggleQuestion(question) {
    const questionId = this.getQuestionToggleIdentifier(question);
    this.setState((state) => ({
      expandedQuestions: {
        ...state.expandedQuestions,
        [questionId]: state.expandedQuestions[questionId] === false
      }
    }));
  }

  handleSetAllQuestionsExpanded(comparison, expanded) {
    const expandedQuestions = {};
    this.getQuestionToggleIdentifiers(comparison).forEach((questionId) => {
      expandedQuestions[questionId] = expanded;
    });
    this.setState({ expandedQuestions });
  }

  createQuestionComparisonTable(res) {
    this.createComparisonTable(
      this.getQuestionsWithPageNames(this.getPages()),
      this.getQuestionsWithPageNames(this.getOldPages()),
      res,
      1
    );
  }

  getQuestionsWithPageNames(pages) {
    return (pages ?? []).flatMap((page) => (
      (page.questions ?? []).map((question) => ({
        ...question,
        comparePageName: page.name
      }))
    ));
  }

  showDuplicateKeysWarningIfNeeded() {
    if (this.duplicateKeysWarningShown) {
      return;
    }

    const { res, isLastVersionSelected } = this.buildSurveyComparison();

    if (isLastVersionSelected && this.detectDuplicateKeysInChildren(res)) {
      this.duplicateKeysWarningShown = true;
      this.props.messagesReceived(createWarningMessages("template.activation.duplicate_key"));
    }
  }

  showDuplicatePageKeysWarningIfNeeded() {
    const surveys = [this.state.surveyVersion, this.state.surveyVersionToCompare].filter(Boolean);
    const surveyWithDuplicatePageKeys = surveys.find((survey) => this.hasDuplicatePageKeys(survey));

    if (!surveyWithDuplicatePageKeys) {
      return;
    }

    const warningKey = surveys
      .map((survey) => this.getSurveySelectionKey(survey))
      .join("|");

    if (this.duplicatePageKeysWarningShownFor.has(warningKey)) {
      return;
    }

    this.duplicatePageKeysWarningShownFor.add(warningKey);
    this.props.messagesReceived(createWarningMessages("survey.version.compare.warning.duplicate.page.keys"));
  }

  hasDuplicatePageKeys(survey) {
    const pageKeys = (survey?.surveyTemplate?.pages || [])
      .map((page) => page?.key)
      .filter((key) => !!key);

    return new Set(pageKeys).size !== pageKeys.length;
  }

  render() {
    const { propertyChange, changeQuestionField, editPage, surveyName } = this.props;
    const { surveyVersion, surveyVersionToCompare, comparisonMode, comparisonOptions, loadingSelection } = this.state;
    const surveyGroups = this.buildSurveyGroups(comparisonOptions);
    const { res, isLastVersionSelected } = this.buildSurveyComparison();
    
    return (
      <StandardView
        id="survey-compare"
        title={surveyName}
        actions={[
          {
            labelKey: "templateList.title",
            icon: <FontAwesomeIcon icon={faArrowLeft} className="me-2" />,
            onClick: this.callShowModal.bind(this),
            variant: "light"
          },
          {
            labelKey: "survey.editor.save",
            icon: <FontAwesomeIcon icon={faSave} className="me-2" />,
            onClick: this.onSaveSurvey.bind(this)
          }
        ]}
      >
        
        {comparisonOptions.length > 1 &&
          <Row>
            <Col>
            <FormGroup  className="mb-3">
              <FormSelect
                className="mb-2"
                onChange={(event) => this.handleSurveyNameSelection("surveyVersion", event.target.value)}
                value={surveyVersion?.identifier ?? surveyVersion?.surveyId ?? ""}
              >
                {surveyGroups.map((group) => (
                  <option key={group.identifier} value={group.identifier}>
                    {this.getSurveyGroupTitle(group, surveyVersion)}
                  </option>
                ))}
              </FormSelect>
              <FormSelect            
                onChange={(event) => this.handleSurveySelection("surveyVersion", event.target.value)}
                value={surveyVersion ? this.getSurveySelectionKey(surveyVersion) : ""}
              >
                {surveyGroups
                  .find((group) => group.identifier === (surveyVersion?.identifier ?? surveyVersion?.surveyId))
                  ?.surveys.map((survey) => {
                  const optionKey = this.getSurveySelectionKey(survey);

                  return (
                    <option
                      key={optionKey}
                      value={optionKey}
                      disabled={loadingSelection === optionKey}
                    >
                      {this.formatSurveySelectOption(survey)}
                    </option>
                  );
                })}
              </FormSelect>
            </FormGroup>
            </Col>
            <Col>
            <FormGroup  className="mb-3">
              <FormSelect
                className="mb-2"
                onChange={(event) => this.handleSurveyNameSelection("surveyVersionToCompare", event.target.value)}
                value={surveyVersionToCompare?.identifier ?? surveyVersionToCompare?.surveyId ?? ""}
              >
                {surveyGroups.map((group) => (
                  <option key={group.identifier} value={group.identifier}>
                    {this.getSurveyGroupTitle(group, surveyVersionToCompare)}
                  </option>
                ))}
              </FormSelect>
              <FormSelect       
                onChange={(event) => this.handleSurveySelection("surveyVersionToCompare", event.target.value)}
                value={surveyVersionToCompare ? this.getSurveySelectionKey(surveyVersionToCompare) : ""}
              >
                {surveyGroups
                  .find((group) => group.identifier === (surveyVersionToCompare?.identifier ?? surveyVersionToCompare?.surveyId))
                  ?.surveys.map((survey) => {
                  const optionKey = this.getSurveySelectionKey(survey);

                  return (
                    <option
                      key={optionKey}
                      value={optionKey}
                      disabled={loadingSelection === optionKey}
                    >
                      {this.formatSurveySelectOption(survey)}
                    </option>
                  );
                })}
              </FormSelect>
            </FormGroup>
            </Col>
          </Row>
        }

        <ButtonGroup className="mb-3">
          <ToggleButtonGroup
            type="radio"
            name="comparisonMode"
            value={comparisonMode}
            onChange={(value) => this.setState({ comparisonMode: value })}
          >
            <ToggleButton id="comparison-mode-pages" variant="light" value={COMPARISON_MODE_PAGES}>
              {this.props.intl.formatMessage({ id: "survey.version.compare.view.pages" })}
            </ToggleButton>
            <ToggleButton id="comparison-mode-questions" variant="light" value={COMPARISON_MODE_QUESTIONS}>
              {this.props.intl.formatMessage({ id: "survey.version.compare.view.questions" })}
            </ToggleButton>
          </ToggleButtonGroup>
        </ButtonGroup>

        {comparisonMode === COMPARISON_MODE_PAGES && (
          <ButtonGroup className="mb-3 ms-2">
            <Button variant="light" onClick={() => this.handleSetAllPagesExpanded(res, true)}>
              <FontAwesomeIcon icon={faChevronDown} className="me-2" />
              {this.props.intl.formatMessage({ id: "survey.version.compare.expand.all.pages" })}
            </Button>
            <Button variant="light" onClick={() => this.handleSetAllPagesExpanded(res, false)}>
              <FontAwesomeIcon icon={faChevronUp} className="me-2" />
              {this.props.intl.formatMessage({ id: "survey.version.compare.collapse.all.pages" })}
            </Button>
          </ButtonGroup>
        )}

        {comparisonMode === COMPARISON_MODE_QUESTIONS && (
          <ButtonGroup className="mb-3 ms-2">
            <Button variant="light" onClick={() => this.handleSetAllQuestionsExpanded(res, true)}>
              <FontAwesomeIcon icon={faChevronDown} className="me-2" />
              {this.props.intl.formatMessage({ id: "survey.version.compare.expand.all.questions" })}
            </Button>
            <Button variant="light" onClick={() => this.handleSetAllQuestionsExpanded(res, false)}>
              <FontAwesomeIcon icon={faChevronUp} className="me-2" />
              {this.props.intl.formatMessage({ id: "survey.version.compare.collapse.all.questions" })}
            </Button>
          </ButtonGroup>
        )}
          
        <div className="survey-compare-table-container">
          <SurveyVersionComparisonTable
            surveyComparison={res}
            latestSurvey={surveyVersionToCompare}
            lastApprovedSurvey={surveyVersion}
            propertyChange={propertyChange}
            changeQuestionField={changeQuestionField}
            editPage={editPage}
            currentIdentifier={this.props.identifier}
            currentVersion={this.props.surveyVersionNumber}
            isLastVersionSelected={isLastVersionSelected}
            isPageExpanded={(page) => this.isPageExpanded(page)}
            onTogglePage={(page) => this.handleTogglePage(page)}
            isQuestionExpanded={(question) => this.isQuestionExpanded(question)}
            onToggleQuestion={(question) => this.handleToggleQuestion(question)}
          />
        </div>
      </StandardView>
    );
  }

  formatSurveySelectOption(survey) {
    const modifiedDate = survey.lastModifiedDate
      ? moment(survey.lastModifiedDate).format("DD.MM.YYYY HH:mm")
      : "";

    return `${this.props.intl.formatMessage({ id: "templateList.version" })} ${survey.version}${modifiedDate ? ` (${modifiedDate})` : ""}`;
  }

  onSaveSurvey() {
    const { identifier, surveyName, surveyAdminLink, saveSurvey } = this.props;
    saveSurvey(identifier, surveyName, surveyAdminLink);
  }

  callShowModal() {
    const { showDialog } = this.props;
    //when user click go back button, we are triggering [ducks].showDialog
    showDialog(
      { action: "/userArea/templates" },
      null,
      "dialog.template.leave.title",
      "dialog.template.leave.text",
      "dialog.leave",
      "dialog.dismiss",
      <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
    );
  }

  /**
   * looks through two arrays and compares keys for the elements
   * @param a the newer version of elements (newer survey version)
   * @param b the older version of elements (older survey version)
   */
  createComparisonTable(a, b, res, typeIndex, pageName, questionName, choiceName, choiceTarget) {
    // rows and columns should have a dedicated headline
    if (choiceTarget === "rows" || choiceTarget === "columns") {
      res.push({
        emptyKey: choiceTarget,
        change: ["row-col-identifier"],
        pageName,
        questionName,
        choiceName,
        choiceTarget
      });
    }

    // merge the keys of both the arrays
    const mergedKeys = _.union(
      a.map((elem) => {
        return this.getComparisonIdentifier(elem, typeIndex);
      }),
      b.map((elem) => {
        return this.getComparisonIdentifier(elem, typeIndex);
      })
    );

    const questions = typeIndex === 1 ? getQuestionsFromSurvey({pages: this.getPages()}) : [];
    const oldQuestions = typeIndex === 1 ? getQuestionsFromSurvey({pages: this.getOldPages()}) : [];
    // check in which array the key exists
    mergedKeys.forEach((comparisonIdentifier) => {
      const newElem = _.find(a, (elem) => this.getComparisonIdentifier(elem, typeIndex) === comparisonIdentifier);
      const oldElem = _.find(b, (elem) => this.getComparisonIdentifier(elem, typeIndex) === comparisonIdentifier);
      const questionsWithSameKey = questions.filter(q => q.key === newElem?.key);
      const oldQuestionsWithSameKey = oldQuestions.filter(q => q.key === oldElem?.key);

      const row = {
        keyNew: newElem?.key,
        keyOld: oldElem?.key,
        oldKeyAlreadyUsed: typeIndex !== 1
          ? this.hasDuplicateKey(b, oldElem)
          : this.hasDuplicateKey(oldQuestionsWithSameKey, oldElem),
        newKeyAlreadyUsed: typeIndex !== 1
          ? this.hasDuplicateKey(a, newElem)
          : this.hasDuplicateKey(questionsWithSameKey, newElem),
        pageName: pageName || this.getElementPageName(newElem, oldElem, typeIndex),
        questionName: questionName || (typeIndex === 1 ? this.getElementQuestionName(newElem, oldElem) : null),
        choiceName: choiceName || (typeIndex > 1 ? newElem?.name : null),
        choiceTarget: choiceTarget,
        nameOld: oldElem?.name,
        nameNew: newElem?.name,
        titleOld: typeIndex > 1 ? oldElem?.text : oldElem?.title,
        titleNew: typeIndex > 1 ? newElem?.text : newElem?.title,
        entity: typeIndex === 0 && newElem ? newElem : null,
        dataTypeOld: oldElem?.type,
        dataTypeNew: newElem?.type,
        type: Array.isArray(typeOrder[typeIndex])
          ? typeOrder[typeIndex][choiceTarget === "rows" ? 0 : choiceTarget === "columns" ? 1 : 2]
          : typeOrder[typeIndex],
        children: [],
        typeIndex: typeIndex
      };

      if (newElem && oldElem) {
        // no key change
        row.change = [];

        // other changes
        if (SurveyUtils.isTextChanged(newElem.title, oldElem.title) || SurveyUtils.isTextChanged(newElem.text, oldElem.text)) {
          row.change.push("nameChange");
        }
        if (newElem.type && oldElem.type && newElem.type !== oldElem.type) {
          row.change.push("typeChange");
        }
      } else if (newElem) {
        // new element
        row.change = ["new"];
      } else {
        // deleted element
        row.change = ["deleted"];
      }
      res.push(row);

      // check for predefined List as we dont want to compare these lists
      if (newElem?.type === QuestionTypes.predefinedList) {
        newElem.choices = [];
      }
      if (oldElem?.type === QuestionTypes.predefinedList) {
        oldElem.choices = [];
      }

      // go to next layer
      if (typeIndex < typeOrder.length - 1) {
        if (Array.isArray(typeOrder[typeIndex + 1])) {
          const rows = typeOrder[typeIndex + 1][0];
          const cols = typeOrder[typeIndex + 1][1];
          const choices = typeOrder[typeIndex + 1][2];

          if (newElem && newElem[choices]) {
            this.createComparisonTable(
              newElem && newElem[choices] ? newElem[choices] : [],
              oldElem && oldElem[choices] ? oldElem[choices] : [],
              res[res.length - 1].children,
              typeIndex + 1,
              pageName || newElem?.name,
              questionName || (typeIndex === 1 ? newElem?.name : null),
              choiceName || (typeIndex > 1 ? newElem?.name : null),
              typeIndex === 3 ? "tempChoices" : null
            );
          }

          if (newElem && newElem[rows]) {
            this.createComparisonTable(
              newElem && newElem[rows] ? newElem[rows] : [],
              oldElem && oldElem[rows] ? oldElem[rows] : [],
              res[res.length - 1].children,
              typeIndex + 1,
              pageName || newElem?.name,
              questionName || (typeIndex === 1 ? newElem?.name : null),
              choiceName || (typeIndex > 1 ? newElem?.name : null),
              "rows"
            );
          }

          if (newElem && newElem[cols]) {
            this.createComparisonTable(
              newElem && newElem[cols] ? newElem[cols] : [],
              oldElem && oldElem[cols] ? oldElem[cols] : [],
              res[res.length - 1].children,
              typeIndex + 1,
              pageName || newElem?.name,
              questionName || (typeIndex === 1 ? newElem?.name : null),
              choiceName || (typeIndex > 1 ? newElem?.name : null),
              "columns"
            );
          }
        } else {
          this.createComparisonTable(
            newElem && newElem[typeOrder[typeIndex + 1]] ? newElem[typeOrder[typeIndex + 1]] : [],
            oldElem && oldElem[typeOrder[typeIndex + 1]] ? oldElem[typeOrder[typeIndex + 1]] : [],
            res[res.length - 1].children,
            typeIndex + 1,
            pageName || newElem?.name,
            questionName || (typeIndex === 1 ? newElem?.name : null),
            choiceName || (typeIndex > 1 ? newElem?.name : null),
            null
          );
        }
      }
    });
  }

  getComparisonIdentifier(elem, typeIndex) {
    if (typeIndex === 0) {
      return elem?.key ?? elem?.name;
    }

    if (typeIndex === 1) {
      return elem?.key ?? elem?.name;
    }

    return elem?.name;
  }

  hasDuplicateKey(elements, elem) {
    if (!elem?.key) {
      return false;
    }

    return elements.filter((element) => element?.key === elem.key).length > 1;
  }

  getElementPageName(newElem, oldElem, typeIndex) {
    if (typeIndex === 0) {
      return newElem?.name ?? oldElem?.name;
    }

    return newElem?.comparePageName ?? oldElem?.comparePageName ?? newElem?.name ?? oldElem?.name;
  }

  getElementQuestionName(newElem, oldElem) {
    return newElem?.name ?? oldElem?.name ?? null;
  }

  detectChangesInChildren(comparisonTable) {
    let res = false;

    comparisonTable.forEach((row) => {
      let changesPresent = false;

      // drill down and check if changes happened in children
      if (row.children && row.children.length > 0) {
        changesPresent = this.detectChangesInChildren(row.children);

        // if changes in children happened push "otherChange" onto list
        if (changesPresent) {
          row.change.push("otherChange");
        }
      }

      // at the end check for changes and set res if found
      if (changesPresent || (row.change.length > 0 && !row.change.includes("row-col-identifier"))) {
        res = true;
      }
    });

    return res;
  }

  detectDuplicateKeysInChildren(comparisonTable) {
    if (comparisonTable) {
      for (const row of comparisonTable) {
        if (
          (row.typeIndex !== 0 && row.keyNew && row.newKeyAlreadyUsed) ||
          this.detectDuplicateKeysInChildren(row.children)
        ) {
          return true;
        }
      };
    }
    
    return false;
  }
}

SurveyVersionCompare.propTypes = {
  identifier: PropTypes.string,
  latestSurvey: PropTypes.object,
  lastApprovedSurvey: PropTypes.object,
  surveyVersionNumber: PropTypes.number,
  surveys: PropTypes.array,
  templateList: PropTypes.array,
};

export default injectIntl(SurveyVersionCompare);
